// Home: what's sending now, drafts to finish, and what went out. "New email" starts from a design.

import { useEffect, useMemo, useState } from 'preact/hooks';
import { api } from '../lib/api';
import { linkClick, navigate, useLocation } from '../lib/router';
import { Button, Empty, LoadError, Modal, Progress, Spinner, StatusChip, confirmBox, toast, toastError, useLoad } from '../lib/ui';
import { Icon } from '../lib/icons';
import { ago, eta, greeting, num, pct, plural, when } from '../lib/format';
import { useSettings } from '../lib/store';
import { MiniEmail, previewHtml } from '../lib/preview';
import { starter, blank } from '../../shared/templates';
import { UpNext } from './Year';
import { ReadyToBook, type Lead } from './ReportLive';
import { LOOK_IDS, getLook } from '../../shared/looks';
import type { EmailStats, EmailSummary, LookId } from '../../shared/types';
import { PURPOSES, purposeLabel, purposeOfKey, type Purpose } from '../../shared/library/purpose';

type Tab = 'all' | 'draft' | 'scheduled' | 'sent';
type Kind = Purpose | 'own';
type Sort = 'new' | 'old' | 'az' | 'taps';

const KINDS: { id: Kind; label: string }[] = [...PURPOSES.map((c) => ({ id: c.id as Kind, label: c.label })), { id: 'own', label: 'Your own' }];

/** What this email is for: the group of the ready-made email it started from, or "Your own". */
const kindOf = (e: Pick<EmailSummary, 'library_key'>): Kind => purposeOfKey(e.library_key);

const kindLabel = (k: Kind) => purposeLabel(k);
const tapRate = (e: EmailSummary) => (e.sent ? e.clicked / e.sent : -1);

export function emailHref(e: Pick<EmailSummary, 'id' | 'status'>): string {
  if (e.status === 'draft') return `/emails/${e.id}/edit`;
  if (e.status === 'scheduled') return `/emails/${e.id}/send`;
  return `/emails/${e.id}`;
}

export function Emails() {
  const { query } = useLocation();
  const list = useLoad(() => api<{ emails: EmailSummary[] }>('/api/emails').then((r) => r.emails));
  const people = useLoad(() => api<{ byStatus: Record<string, number> }>('/api/contacts?page=0').then((r) => r.byStatus.active));
  const leads = useLoad(() => api<{ leads: Lead[] }>('/api/leads?days=14').then((r) => r.leads));
  const [tab, setTab] = useState<Tab>('all');
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<Kind | 'all'>('all');
  const [sort, setSort] = useState<Sort>('new');
  const [etas, setEtas] = useState<Record<number, number | null>>({});
  const newOpen = query.get('new') === '1';

  const emails = list.data ?? [];
  const sending = emails.filter((e) => e.status === 'sending' || e.status === 'paused');
  const live = sending.some((e) => e.status === 'sending');

  // Refresh while something is sending.
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => list.reload(), 15_000);
    return () => clearInterval(t);
  }, [live]);

  useEffect(() => {
    for (const e of sending) {
      api<{ stats: EmailStats }>(`/api/emails/${e.id}/stats`)
        .then((r) => setEtas((m) => ({ ...m, [e.id]: r.stats.etaMinutes })))
        .catch(() => {});
    }
  }, [sending.map((e) => `${e.id}:${e.sent}`).join()]);

  const counts = useMemo(
    () => ({
      all: emails.filter((e) => !['automated', 'sending', 'paused'].includes(e.status)).length,
      draft: emails.filter((e) => e.status === 'draft').length,
      scheduled: emails.filter((e) => e.status === 'scheduled').length,
      sent: emails.filter((e) => ['sent', 'canceled'].includes(e.status)).length,
    }),
    [emails],
  );
  const inTab = emails.filter((e) => {
    if (e.status === 'automated') return false;
    if (e.status === 'sending' || e.status === 'paused') return false;
    if (tab === 'all') return true;
    if (tab === 'sent') return e.status === 'sent' || e.status === 'canceled';
    return e.status === tab;
  });
  const kindsHere = KINDS.filter((k) => inTab.some((e) => kindOf(e) === k.id));
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = inTab.filter((e) => {
    if (kind !== 'all' && kindOf(e) !== kind) return false;
    const hay = `${e.name} ${e.subject} ${kindLabel(kindOf(e))}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  if (sort === 'old') shown.reverse();
  else if (sort === 'az') shown.sort((a, b) => a.name.localeCompare(b.name));
  else if (sort === 'taps') shown.sort((a, b) => tapRate(b) - tapRate(a));
  const filtering = words.length > 0 || kind !== 'all';

  // Good news first: people who want to book. The draft count is already on the Drafts tab, so it shows only when nothing else does.
  const ready = leads.data?.length ?? 0;
  const news = [ready > 0 && `${plural(ready, 'client')} ${ready === 1 ? 'wants' : 'want'} to book.`, sending.length > 0 && `${sending.length} sending.`, counts.scheduled > 0 && `${counts.scheduled} scheduled.`].filter(Boolean).join(' ');
  const sub = news || (counts.draft ? `${plural(counts.draft, 'draft')} waiting.` : '');

  return (
    <div class="wrap home-page">
      <div class="page-head">
        <div>
          <h1 class="display">{greeting()}</h1>
          <p class="lead">{list.data ? sub || 'Nothing in progress.' : ' '}</p>
        </div>
        <div class="head-actions">
          <Button icon="calendar" onClick={() => navigate('/year')}>
            Pick a ready email
          </Button>
        </div>
      </div>

      {list.error && <LoadError error={list.error} retry={list.reload} />}
      {!list.data && !list.error && <Spinner />}

      {list.data && (
        <>
          {people.data === 0 && <GettingStarted hasEmail={emails.length > 0} />}

          {sending.map((e) => (
            <a key={e.id} href={`/emails/${e.id}`} class="hot-card" onClick={(ev) => linkClick(ev, `/emails/${e.id}`)}>
              <div class="hot-top">
                <span>
                  <StatusChip status={e.status} />
                </span>
                <span class="hot-link">
                  Open report <Icon name="chevron" size={16} />
                </span>
              </div>
              <div class="hot-title">{e.name}</div>
              <div class="hot-bottom">
                <span>
                  {num(e.sent)} of {num(e.total)} sent
                  {e.status === 'sending' && etas[e.id] != null && ` · ${eta(etas[e.id])}`}
                  {e.status === 'paused' && e.problem && ` · ${e.problem}`}
                </span>
                <span class="hot-pct">{pct(e.sent, e.total)}</span>
              </div>
              <Progress value={e.sent} max={e.total} label="Sent so far" />
            </a>
          ))}

          {people.data !== 0 && <UpNext emails={emails} />}

          {leads.data && <ReadyToBook leads={leads.data} showEmail limit={3} />}

          {counts.all > 0 && (
            <div class="tabs" role="tablist">
              {(
                [
                  ['all', 'All'],
                  ['draft', 'Drafts'],
                  ['scheduled', 'Scheduled'],
                  ['sent', 'Sent'],
                ] as [Tab, string][]
              ).map(([k, label]) => (
                <button key={k} role="tab" aria-selected={tab === k} class={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
                  {label}
                  {k !== 'all' && counts[k] > 0 && <i>{counts[k]}</i>}
                </button>
              ))}
            </div>
          )}

          {counts.all >= 4 && (
            <div class="email-tools">
              <div class="email-tools-top">
                <label class="lib-search">
                  <Icon name="search" size={18} />
                  <input type="search" value={q} placeholder="Search" aria-label="Search your emails" onInput={(e) => setQ(e.currentTarget.value)} />
                </label>
                <select class="select" value={sort} aria-label="Sort" onChange={(e) => setSort(e.currentTarget.value as Sort)}>
                  <option value="new">Newest first</option>
                  <option value="old">Oldest first</option>
                  <option value="az">A to Z</option>
                  <option value="taps">Most taps</option>
                </select>
              </div>
              {kindsHere.length > 1 && (
                <div class="lib-chips" role="group" aria-label="Kind of email">
                  <button class={`lib-chip ${kind === 'all' ? 'on' : ''}`} aria-pressed={kind === 'all'} onClick={() => setKind('all')}>
                    All
                  </button>
                  {kindsHere.map((k) => (
                    <button key={k.id} data-cat={k.id} class={`lib-chip ${kind === k.id ? 'on' : ''}`} aria-pressed={kind === k.id} onClick={() => setKind(kind === k.id ? 'all' : k.id)}>
                      <i />
                      {k.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {shown.length === 0 && filtering ? (
            <div class="email-none">
              <p class="muted">Nothing matches.</p>
              <Button
                kind="quiet"
                icon="x"
                onClick={() => {
                  setQ('');
                  setKind('all');
                }}
              >
                Clear
              </Button>
            </div>
          ) : shown.length > 0 ? (
            <div class="email-list">
              {shown.map((e) => (
                <EmailRowItem key={e.id} e={e} onChange={list.reload} />
              ))}
            </div>
          ) : counts.all === 0 && !sending.length ? (
            <Empty icon="mail" title="Your first email is a few taps away" action={<Button kind="primary" icon="plus" onClick={() => navigate('/?new=1')}>Write an email</Button>}>
              Pick a design, change the words and photos, and send it to your clients.
            </Empty>
          ) : (
            shown.length === 0 && counts.all > 0 && <p class="muted pad">Nothing here yet.</p>
          )}
        </>
      )}

      {newOpen && <NewEmail onClose={() => navigate('/', { replace: true, keepScroll: true })} />}
    </div>
  );
}

function GettingStarted({ hasEmail }: { hasEmail: boolean }) {
  return (
    <div class="start-card">
      <h2>Two steps to your first send</h2>
      <a href="/contacts/import" class="start-step" onClick={(e) => linkClick(e, '/contacts/import')}>
        <span class="start-num">1</span>
        <span>
          <b>Add your clients</b>
          <small>Drop in the export from Aesthetic Record or any spreadsheet.</small>
        </span>
        <Icon name="chevron" />
      </a>
      <a href="/?new=1" class={`start-step ${hasEmail ? 'done' : ''}`} onClick={(e) => linkClick(e, '/?new=1')}>
        <span class="start-num">{hasEmail ? <Icon name="check" size={16} stroke={2.6} /> : 2}</span>
        <span>
          <b>Write your first email</b>
          <small>Start from one of five designs made for LaVir.</small>
        </span>
        <Icon name="chevron" />
      </a>
    </div>
  );
}

function rowMeta(e: EmailSummary): string {
  switch (e.status) {
    case 'draft':
      return `Edited ${ago(e.updated_at)}`;
    case 'scheduled':
      return `Goes out ${when(e.scheduled_at)}`;
    case 'sent':
      return `Sent ${when(e.finished_at || e.started_at)} to ${plural(e.sent, 'person', 'people')}`;
    case 'canceled':
      return `Stopped after ${plural(e.sent, 'email')}`;
    default:
      return '';
  }
}

function EmailRowItem({ e, onChange }: { e: EmailSummary; onChange: () => void }) {
  const [menu, setMenu] = useState(false);
  const href = emailHref(e);
  const started = e.total > 0;

  const duplicate = async () => {
    setMenu(false);
    try {
      const { id } = await api<{ id: number }>(`/api/emails/${e.id}/duplicate`, { method: 'POST' });
      toast('Copy made. Opening it.');
      navigate(`/emails/${id}/edit`);
    } catch (err) {
      toastError(err);
    }
  };
  const remove = async () => {
    setMenu(false);
    if (!(await confirmBox({ title: `Delete "${e.name}"?`, body: 'This draft will be gone for good.', ok: 'Delete', danger: true }))) return;
    try {
      await api(`/api/emails/${e.id}`, { method: 'DELETE' });
      toast('Draft deleted.');
      onChange();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div class="email-row">
      <a href={href} class="email-row-main" onClick={(ev) => linkClick(ev, href)}>
        <div class="thumb" style={{ background: getLook(e.look).outer }}>{e.thumb ? <img src={e.thumb} alt="" loading="lazy" /> : <Icon name="mail" size={22} />}</div>
        <div class="email-row-text">
          <div class="email-row-title">{e.name}</div>
          <div class="email-row-sub">{e.subject || <span class="muted">No subject yet</span>}</div>
          <div class="email-row-meta">
            {kindOf(e) !== 'own' && (
              <span class="row-kind" data-cat={kindOf(e)}>
                <i />
                {kindLabel(kindOf(e))}
              </span>
            )}
            {rowMeta(e)}
          </div>
          {e.problem && (
            <div class="email-row-problem">
              <Icon name="alert" size={14} /> {e.problem}
            </div>
          )}
        </div>
        {started ? (
          <div class="nums">
            <div>
              <b>{num(e.sent)}</b>
              <small>Sent</small>
            </div>
            <div>
              <b>{pct(e.opened, e.sent)}</b>
              <small>Opened</small>
            </div>
            <div>
              <b>{pct(e.clicked, e.sent)}</b>
              <small>Clicked</small>
            </div>
          </div>
        ) : (
          <div class="nums nums-empty" />
        )}
        <StatusChip status={e.status} />
      </a>
      <div class="row-menu">
        <button class="icon-btn" aria-label="More" aria-expanded={menu} onClick={() => setMenu(!menu)}>
          <Icon name="more" size={18} />
        </button>
        {menu && (
          <>
            <div class="menu-scrim" onClick={() => setMenu(false)} />
            <div class="menu" role="menu">
              <button role="menuitem" onClick={duplicate}>
                <Icon name="copy" size={16} /> Make a copy
              </button>
              {e.status === 'draft' && !e.sequence && (
                <button role="menuitem" class="danger" onClick={remove}>
                  <Icon name="trash" size={16} /> Delete
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function NewEmail({ onClose }: { onClose: () => void }) {
  const s = useSettings();
  const [busy, setBusy] = useState<string | null>(null);
  const previews = useMemo(() => (s ? LOOK_IDS.map((id) => ({ id, html: previewHtml(starter(id), s.settings) })) : []), [s]);
  const blankHtml = useMemo(() => (s ? previewHtml(blank('onething'), s.settings) : ''), [s]);

  const make = async (look: LookId, isBlank = false) => {
    setBusy(isBlank ? 'blank' : look);
    try {
      const { id } = await api<{ id: number }>('/api/emails', { body: { look, blank: isBlank } });
      navigate(`/emails/${id}/edit`, { replace: true });
    } catch (e) {
      toastError(e);
      setBusy(null);
    }
  };

  return (
    <Modal title="Start from a design" onClose={onClose} wide>
      <button class="year-link" onClick={() => navigate('/year', { replace: true })}>
        <Icon name="calendar" size={18} />
        <span class="grow">
          <b>Written for you</b>
          <small>100 ready emails with photos. Open one and send it.</small>
        </span>
        <Icon name="chevron" size={16} />
      </button>
      <p class="muted modal-lead">Or start from a design. Each one is filled in with a sample you can change.</p>
      {!s ? (
        <Spinner />
      ) : (
        <div class="look-grid">
          {previews.map((p) => {
            const L = getLook(p.id);
            return (
              <button key={p.id} class="look-card" onClick={() => make(p.id)} disabled={!!busy}>
                <div class="look-pic" style={{ background: L.outer }}>
                  <MiniEmail html={p.html} width={220} height={250} />
                  {busy === p.id && (
                    <div class="look-busy">
                      <span class="spin" />
                    </div>
                  )}
                </div>
                <b>{L.name}</b>
                <small>{L.description}</small>
              </button>
            );
          })}
          <button class="look-card" onClick={() => make('onething', true)} disabled={!!busy}>
            <div class="look-pic look-blank">
              <MiniEmail html={blankHtml} width={220} height={250} />
              {busy === 'blank' && (
                <div class="look-busy">
                  <span class="spin" />
                </div>
              )}
            </div>
            <b>Blank</b>
            <small>A headline, a message, and a button.</small>
          </button>
        </div>
      )}
    </Modal>
  );
}
